import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../contexts/auth.context";
import { useApi } from "../../../hooks/api.hook";
import UserAvatar from "../../../components/generic/UserAvatar";
import { cn, searchInArray } from "../../../util/base.util";
import { CheckSquare, Pin, Square } from "lucide-react";
import { useNavigate } from "react-router";
import Masonry from "react-masonry-css";
import { INote } from "../../../shared/type/list.type";

function View({list}: {list: INote}) {

    const nav = useNavigate();

    return (
        <div onClick={() => nav(`/lists/${list.id}`)} key={`list-view-${list.id}`} className="p-2 border border-[#ffffff33] my-2 rounded-md cursor-pointer hover:bg-base-100 active:bg-neutral active:text-neutral-content">
            <div className="pointer-events-none text-sm mb-1 flex items-center">
                    <p>{list.label}</p>
            </div>
            {list.body != undefined && (
                <div className="pointer-events-none whitespace-pre-wrap text-xs opacity-70 max-h-40 overflow-hidden">
                    {list.body}
                </div>
            )}
            <div className="text-xs opacity-70 text-ellipsis pointer-events-none">
                {/* {list.items.map(item => (
                    <p key={`list-view-item-${item.id}`} className="whitespace-pre text-ellipsis overflow-hidden">{item.label}</p>
                ))} */}
                {list.items.slice(0, 10).map(item => (
                    <div key={`list-view-item-${item.id}`} className="grid items-center gap-2 grid-cols-[auto_1fr]">
                        <div>
                            {item.isChecked ? <CheckSquare size={12} className="opacity-50" /> : <Square size={12} />}
                        </div>
                        <div className="overflow-hidden">
                            <p className={cn(
                                "whitespace-nowrap text-ellipsis overflow-hidden",
                                item.isChecked && "opacity-50"
                            )}>{item.label}</p>
                        </div>
                    </div>
                ))}
                {list.items.length > 10 && (
                    <p>+ {list.items.length - 10} more</p>
                )}
                
                <div className="flex mt-2">
                    <div className="flex-1 flex items-center gap-2">
                        {list.labels.map(label => (
                            <div key={`note-${list.id}-label-${label.id}`} className="badge badge-xs badge-soft">{label.value}</div>
                        ))}
                    </div>

                    <div className="flex items-center justify-end gap-2">
                        <div>
                            <UserAvatar id={list.ownerId} size="sm" />
                        </div>
                        <div>
                            <p>{list.owner?.profile?.firstName ?? list.owner?.username} {list.owner?.profile?.lastName}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const NoteSort = (a: INote,b: INote) => {
    const aLatest = new Date(a.modifiedAt || a.createdAt);
    const aLatestItem = a.items.length === 0 ? null : new Date(a.items.slice(-1)[0].createdAt);
    const bLatest = new Date(b.modifiedAt || b.createdAt);
    const bLatestItem = b.items.length === 0 ? null : new Date(b.items.slice(-1)[0].createdAt);
    const aFinal = aLatestItem === null || aLatest.getTime() > aLatestItem.getTime() ? aLatest : aLatestItem;
    const bFinal = bLatestItem === null || bLatest.getTime() > bLatestItem.getTime() ? bLatest : bLatestItem;

    return bFinal.getTime() - aFinal.getTime();
}

export default function NotesView({layout, search}: {
    layout: "grid"|"list";
    search: string;
}) {
    const auth = useContext(AuthContext);
    const listService = useApi<INote[]>("list")

    const [sortedNotes, setSortedNotes] = useState<(INote)[]>([]);
    const [pinnedNotes, setPinnedNotes] = useState<(INote)[]>([]);

    useEffect(() => {
        if (!listService.data || !auth.user) return;

        let notes: INote[] = [];
        let _pinned: INote[] = [];

        for (const note of listService.data) {
            if (note.pinnedBy!.map(v => v.id).includes(auth.user.id)) {
                _pinned.push(note);
            } else {
                notes.push(note);
            }
        }

        notes.sort(NoteSort);
        _pinned.sort(NoteSort);

        if (search) {
            notes = notes.filter(v => v.label?.toLowerCase().includes(search.toLowerCase()) || v.body?.toLowerCase().includes(search.toLowerCase()) || searchInArray(search, v.items.map(w => w.label)));
            _pinned = _pinned.filter(v => v.label?.toLowerCase().includes(search.toLowerCase()) || v.body?.toLowerCase().includes(search.toLowerCase()) || searchInArray(search, v.items.map(w => w.label))) ?? [];
        }

        setSortedNotes(notes);
        setPinnedNotes(_pinned);
        
    }, [search, listService.data, auth.user]);

    useEffect(() => {

        if (!auth.user) return;

        listService.get()

        // let notes = auth.user.lists!.concat(auth.user.sharedLists!).filter(v => !auth.user!.pinnedNotes!.map(v => v.id).includes(v.id)).sort(NoteSort);
        // // let notes = [...(auth.user?.lists ?? [])].concat([...(auth.user?.sharedLists ?? [])]).filter(v => !hiddenNoteIds.includes(v.id)).sort(NoteSort);
        // let _pinnedNotes = auth.user.pinnedNotes!.sort(NoteSort);

        // if (search) {
        //     notes = notes.filter(v => v.label.toLowerCase().includes(search.toLowerCase()) || v.body?.toLowerCase().includes(search.toLowerCase()) || searchInArray(search, v.items.map(w => w.label)));
        //     _pinnedNotes = auth.user.pinnedNotes!.filter(v => v.label.toLowerCase().includes(search.toLowerCase()) || v.body?.toLowerCase().includes(search.toLowerCase()) || searchInArray(search, v.items.map(w => w.label)));
        // }

        // setSortedNotes(notes);
        // setPinnedNotes(_pinnedNotes);
    }, [search, auth.user])

    if (layout === 'list') return (
        <>
        {pinnedNotes.length > 0 && (
            <>
            <p className="opacity-50 text-xs">Pinned</p>
            {pinnedNotes.map(note => (
                <View key={`list-view-pinned-${note.id}`} list={note} />
            ))}
            <p className="opacity-50 text-xs">Other</p>
            </>
        )}
        
        {sortedNotes.filter(note => !auth.user?.pinnedNotes?.map(v => v.id).includes(note.id)).map(note => (
            <View key={`list-view-${note.id}`} list={note} />
        ))}
        </>
    );

    //grid view
    return (
        <>
        {pinnedNotes.length > 0 && (
            <>
            <p className="opacity-50 text-xs">Pinned</p>
            <Masonry breakpointCols={2} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
                {pinnedNotes.map(note => (
                    <View key={`list-view-pinned-${note.id}`} list={note} />
                ))}
            </Masonry>
            <p className="opacity-50 text-xs">Other</p>
            </>
        )}
        
        <Masonry breakpointCols={2} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
            {sortedNotes.filter(note => !auth.user?.pinnedNotes?.map(v => v.id).includes(note.id)).map(note => (
                <View key={`list-view-${note.id}`} list={note} />
            ))}
        </Masonry>
        </>
    );
}